import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { calcAnalytics } from '../lib/analytics'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'

const StatCard = ({ label, value, sub, color }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
    <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color || 'text-white'}`}>{value ?? '—'}</p>
    {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
  </div>
)

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm">
      <p className="text-gray-400">{label}</p>
      <p className={payload[0].value >= 0 ? 'text-green-400' : 'text-red-400'}>
        {payload[0].value >= 0 ? '+' : ''}{payload[0].value.toFixed(2)}
      </p>
    </div>
  )
}

export default function Analytics() {
  const { user } = useAuth()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [marketFilter, setMarketFilter] = useState('')

  useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      let query = supabase.from('trades').select('*').eq('user_id', user.id).order('entry_date', { ascending: true })
      if (marketFilter) query = query.eq('market_type', marketFilter)
      const { data } = await query
      setStats(calcAnalytics(data || []))
      setLoading(false)
    }
    fetch()
  }, [user.id, marketFilter])

  if (loading) return <div className="p-8 text-gray-400">Loading analytics...</div>
  if (!stats) return (
    <div className="p-8 text-center">
      <p className="text-gray-400 text-lg">No closed trades yet.</p>
      <p className="text-gray-600 text-sm mt-2">Log some trades in the Journal to see your analytics.</p>
    </div>
  )

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-gray-400 text-sm mt-1">Based on {stats.totalTrades} closed trades</p>
        </div>
        <select
          value={marketFilter}
          onChange={e => setMarketFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
        >
          <option value="">All Markets</option>
          <option value="crypto">Crypto</option>
          <option value="forex">Forex</option>
        </select>
      </div>

      {/* Scorecard */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          label="Total P&L"
          value={(stats.totalPnl > 0 ? '+' : '') + stats.totalPnl}
          sub={`${stats.winners}W / ${stats.losers}L`}
          color={stats.totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}
        />
        <StatCard
          label="Win Rate"
          value={stats.winRate + '%'}
          sub={`Avg win: ${stats.avgWin}`}
          color={stats.winRate >= 50 ? 'text-green-400' : 'text-red-400'}
        />
        <StatCard
          label="Profit Factor"
          value={stats.profitFactor}
          sub={stats.profitFactor >= 1.5 ? '✅ Strong' : stats.profitFactor >= 1 ? '⚠️ Marginal' : '❌ Negative'}
          color={stats.profitFactor >= 1.5 ? 'text-green-400' : stats.profitFactor >= 1 ? 'text-yellow-400' : 'text-red-400'}
        />
        <StatCard
          label="Max Drawdown"
          value={'-' + stats.maxDrawdown}
          sub="Peak to trough"
          color="text-red-400"
        />
        <StatCard
          label="Sortino Ratio"
          value={stats.sortinoRatio ?? 'N/A'}
          sub={stats.sortinoRatio ? (stats.sortinoRatio >= 2 ? '✅ Excellent' : stats.sortinoRatio >= 1 ? '⚠️ Acceptable' : '❌ Poor') : 'Need more data'}
          color={stats.sortinoRatio >= 2 ? 'text-green-400' : stats.sortinoRatio >= 1 ? 'text-yellow-400' : 'text-red-400'}
        />
        <StatCard
          label="Streaks"
          value={stats.currentStreak}
          sub={`Best: ${stats.bestWinStreak}W / Worst: ${stats.worstLossStreak}L`}
          color={stats.currentStreak.includes('W') ? 'text-green-400' : 'text-red-400'}
        />
      </div>

      {/* Equity Curve */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h2 className="text-white font-semibold mb-4">Equity Curve</h2>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={stats.equityCurve}>
            <defs>
              <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
            <XAxis dataKey="index" stroke="#4b5563" tick={{ fontSize: 11 }} />
            <YAxis stroke="#4b5563" tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="equity" stroke="#3b82f6" fill="url(#eq)" strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly PnL */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h2 className="text-white font-semibold mb-4">Monthly P&L</h2>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={stats.monthlyPnl}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
            <XAxis dataKey="month" stroke="#4b5563" tick={{ fontSize: 11 }} />
            <YAxis stroke="#4b5563" tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="pnl" radius={[4, 4, 0, 0]}
              fill="#3b82f6"
              label={false}
              shape={(props) => {
                const { x, y, width, height, value } = props
                return <rect x={x} y={y} width={width} height={height} fill={value >= 0 ? '#22c55e' : '#ef4444'} rx={4} />
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per Asset */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-gray-800">
          <h2 className="text-white font-semibold">Performance by Asset</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs uppercase border-b border-gray-800">
              <th className="text-left px-5 py-3">Symbol</th>
              <th className="text-right px-5 py-3">Trades</th>
              <th className="text-right px-5 py-3">Win Rate</th>
              <th className="text-right px-5 py-3">Total P&L</th>
            </tr>
          </thead>
          <tbody>
            {stats.byAsset.map(asset => (
              <tr key={asset.symbol} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                <td className="px-5 py-3 text-white font-medium">{asset.symbol}</td>
                <td className="px-5 py-3 text-right text-gray-400">{asset.trades}</td>
                <td className={`px-5 py-3 text-right ${asset.winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>{asset.winRate}%</td>
                <td className={`px-5 py-3 text-right font-medium ${asset.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {asset.pnl >= 0 ? '+' : ''}{asset.pnl}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  )
}
